import { createSelector, select } from '@ngrx/store';
import type { ArticleEntity, BaseArticleEntityWithEnrichedArticle } from './articles.models';
import { createArticleModelEnrichment } from '../transformer/elements-transformer';
import { pipe } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import {
  selectArticlesEntities,
  selectCurrentArticleUUID,
  selectCurrentArticleCmsId,
  getFetchDate,
} from './articles.selectors';
import type { ArticlesPartialState } from './articles.reducer';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ARTICLE_COMPONENT_MAPPING, ComponentMapping } from '@candyland/angular/shared/article-tt-com/model';

export const selectCurrentPreviewArticleEntity = createSelector(
  selectArticlesEntities,
  selectCurrentArticleUUID,
  (articlesEntities, uuid) => articlesEntities[uuid]
);

export const getArticlePreviewByUuid = createSelector<
  ArticlesPartialState,
  { uuid: string },
  Record<string, ArticleEntity>,
  ArticleEntity
>(selectArticlesEntities, (articleEntities: Record<string, ArticleEntity>, props) => articleEntities[props.uuid]);

@Injectable({ providedIn: 'root' })
export class CurrentArticlePreview {
  private selectCurrentEnrichedArticleEntity = createSelector(
    selectCurrentPreviewArticleEntity,
    (articleEntity: ArticleEntity): BaseArticleEntityWithEnrichedArticle =>
      articleEntity && {
        ...articleEntity,
        articleData: articleEntity.articleData && {
          ...articleEntity.articleData,
          ...createArticleModelEnrichment(this.articleComponentMapping, articleEntity.articleData, this.platformId),
        },
      }
  );

  public getArticleEntity = pipe(
    select(this.selectCurrentEnrichedArticleEntity),
    filter((articleEntity: BaseArticleEntityWithEnrichedArticle) => !!articleEntity),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  public getArticleEntityWithError = pipe(
    select(this.selectCurrentEnrichedArticleEntity),
    filter((articleEntity: BaseArticleEntityWithEnrichedArticle) => articleEntity && !!articleEntity.error),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  public getArticle = pipe(
    this.getArticleEntity,
    distinctUntilChanged(
      (prev: BaseArticleEntityWithEnrichedArticle, curr: BaseArticleEntityWithEnrichedArticle) =>
        prev.id === curr.id && getFetchDate(prev) === getFetchDate(curr)
    ),
    map((articleEntity: BaseArticleEntityWithEnrichedArticle) => articleEntity.articleData),
    shareReplay({ refCount: true, bufferSize: 1 })
  );
  public articleCmsId = selectCurrentArticleCmsId;
  public articleUUID = selectCurrentArticleUUID;

  constructor(
    @Inject(ARTICLE_COMPONENT_MAPPING) private articleComponentMapping: ComponentMapping,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}
}
